import { useEffect, useState } from "react";
import api from "../lib/api";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from "recharts";

const COLORS = ["#ef4444", "#f59e0b", "#3b82f6", "#94a3b8"];

export default function AnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/analytics");
        setData(res.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return <div className="px-8 py-10 text-sm text-muted-foreground">Loading analytics…</div>;
  }
  if (!data) return null;

  const priorityData = Object.entries(data.by_priority).map(([k, v]) => ({ name: k, value: v }));
  const dashboardData = Object.entries(data.by_dashboard).map(([k, v]) => ({ name: k, count: v }));

  return (
    <div className="px-4 sm:px-8 py-8 max-w-[1400px] mx-auto">
      <div className="mb-7 anim-fade-up">
        <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">Insights</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight">Analytics</h1>
        <p className="text-sm text-muted-foreground mt-2">Performance at a glance — see what's moving and what's stuck.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-7">
        <Stat label="Total tasks" value={data.total} />
        <Stat label="Completed" value={data.done} accent="text-emerald-500" />
        <Stat label="In progress" value={data.inprogress} accent="text-blue-500" />
        <Stat label="Completion rate" value={`${data.completion_rate}%`} accent="text-primary" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Tasks by priority" testid="chart-priority">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={priorityData} dataKey="value" nameKey="name" outerRadius={90} innerRadius={50}>
                {priorityData.map((entry, idx) => (
                  <Cell key={idx} fill={COLORS[idx % COLORS.length]} stroke="none" />
                ))}
              </Pie>
              <Tooltip contentStyle={tipStyle} />
            </PieChart>
          </ResponsiveContainer>
          <Legend items={priorityData.map((p, i) => ({ label: p.name, value: p.value, color: COLORS[i] }))} />
        </Card>

        <Card title="By dashboard" testid="chart-dashboard">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={dashboardData}>
              <XAxis dataKey="name" tickLine={false} axisLine={false} tick={{ fill: "#888", fontSize: 12 }} />
              <YAxis tickLine={false} axisLine={false} tick={{ fill: "#888", fontSize: 12 }} />
              <Tooltip contentStyle={tipStyle} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
              <Bar dataKey="count" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Last 7 days completed" testid="chart-trend" className="lg:col-span-2">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data.trend}>
              <XAxis dataKey="date" tickLine={false} axisLine={false} tick={{ fill: "#888", fontSize: 11 }} />
              <YAxis tickLine={false} axisLine={false} tick={{ fill: "#888", fontSize: 12 }} />
              <Tooltip contentStyle={tipStyle} />
              <Line type="monotone" dataKey="completed" stroke="hsl(var(--primary))" strokeWidth={2.2} dot={{ r: 3, fill: "hsl(var(--primary))" }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
}

const tipStyle = {
  background: "hsl(var(--card))",
  border: "1px solid hsl(var(--border))",
  borderRadius: "8px",
  fontSize: "12px",
};

function Stat({ label, value, accent }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">{label}</div>
      <div className={`font-display text-3xl font-black tracking-tight ${accent || ""}`}>{value}</div>
    </div>
  );
}

function Card({ title, children, testid, className = "" }) {
  return (
    <div data-testid={testid} className={`rounded-xl border border-border bg-card p-5 ${className}`}>
      <h3 className="font-display font-bold mb-4 text-sm uppercase tracking-wider">{title}</h3>
      {children}
    </div>
  );
}

function Legend({ items }) {
  return (
    <div className="flex flex-wrap gap-3 mt-3 text-xs">
      {items.map((it) => (
        <div key={it.label} className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full" style={{ background: it.color }} />
          <span className="capitalize">{it.label}</span>
          <span className="text-muted-foreground">({it.value})</span>
        </div>
      ))}
    </div>
  );
}
